/**
 * Build with AI: every resource a coding agent uses to build on Vestra, on one page, each with its snippet to
 * copy. Keshav's ruling of 3 October 2026 lets the developer site name the coding tools; copy.test.ts holds
 * the lines that name them to this file (and open-in.ts), and Bash itself is never called by their names.
 * Each address is the site's own, so a staging build points at staging.
 */
import { DOCS_MCP, SKILLS } from "@/lib/developer-markdown";
import type { Lang } from "../_lib/highlight";
import { installsAt } from "./home";

export const BUILD_WITH_AI = "/developers/build-with-ai";

export const BUILD_WITH_AI_TITLE = "Build with AI";
/** Its one line: the search's, llms.txt's and the page's description. */
export const BUILD_WITH_AI_SAYS =
  "Every resource for coding agents: the docs as Markdown, the docs MCP, the Vestra skill, vestra agent setup and vestra mcp.";
export const BUILD_WITH_AI_LEDE =
  "Give your coding agent an objective, and it builds on Vestra: it reads these docs, follows the Vestra skill and runs the CLI. Each resource is one copy away.";

/** Code to copy, under the name the reader knows it by. */
export interface Snippet {
  label: string;
  code: string;
  lang: Lang;
}

export interface Resource {
  id: "docs" | "docs-mcp" | "setup" | "vestra-mcp" | "skill" | "objective";
  title: string;
  /** Paragraphs of the site's own copy: `code` and [links](/developers/ask). */
  body: readonly string[];
  snippets: readonly Snippet[];
}

/** The docs MCP added to each coding tool, each the way its own docs say (read on 3 October 2026, links beside each). */
export function docsMcpAdds(origin: string): readonly Snippet[] {
  const url = `${origin}${DOCS_MCP}`;
  return [
    // https://code.claude.com/docs/en/mcp
    {
      label: "Claude Code",
      code: `claude mcp add --transport http vestra-docs ${url}`,
      lang: "bash",
    },
    // https://cursor.com/docs/context/mcp: the project's .cursor/mcp.json, or ~/.cursor/mcp.json for every project
    {
      label: "Cursor: .cursor/mcp.json",
      code: JSON.stringify({ mcpServers: { "vestra-docs": { url } } }, null, 2),
      lang: "json",
    },
    // https://learn.chatgpt.com/docs/extend/mcp?surface=cli
    {
      label: "Codex",
      code: `codex mcp add vestra-docs --url ${url}`,
      lang: "bash",
    },
    // https://geminicli.com/docs/tools/mcp-server/
    {
      label: "Gemini CLI",
      code: `gemini mcp add --transport http vestra-docs ${url}`,
      lang: "bash",
    },
  ];
}

/** The page's resources, in the order a reader sets them up. */
export function buildWithAi(origin: string): readonly Resource[] {
  return [
    {
      id: "docs",
      title: "The docs, for agents",
      body: [
        "Every page of these docs is Markdown at its own address plus `.md`, and for a request that sends `Accept: text/markdown`. [llms.txt](/developers/llms.txt) lists every page, and [llms-full.txt](/developers/llms-full.txt) is every page in one file.",
        "At the top of each page, its menu copies the page as Markdown, opens its Markdown, or opens it in an assistant.",
      ],
      snippets: [
        {
          label: "Terminal",
          code: [
            `curl ${origin}/developers/llms.txt`,
            `curl ${origin}/developers/llms-full.txt`,
            `curl ${origin}/developers/quickstart.md`,
          ].join("\n"),
          lang: "bash",
        },
      ],
    },
    {
      id: "docs-mcp",
      title: "The docs MCP",
      body: [
        "A public MCP server for these docs: no key, no sign-in, and nothing it can change. `search_docs` finds the sections that answer a question, each with its page; `read_doc` reads a page as Markdown. Both tell the agent to trust the docs over what it remembers.",
        "It speaks MCP over streamable HTTP. Add it to your coding tool once.",
      ],
      snippets: docsMcpAdds(origin),
    },
    {
      id: "setup",
      title: "Set up your coding tools",
      body: [
        "`vestra agent setup` finds Claude Code, Codex, Cursor and Gemini CLI on your computer. For each, it puts the Vestra skill where the tool reads skills, and adds `vestra mcp` as an MCP server, with the tool's own command where it has one.",
        "It shows the plan and asks first, then says what changed. It never writes a key anywhere: `vestra mcp` reads the one `vestra login` saved. Install the CLI first, straight from this site; the [quickstart](/developers/quickstart) has the rest.",
      ],
      snippets: [
        {
          label: "Install the CLI",
          code: installsAt(origin).find((i) => i.door === "cli")!.line,
          lang: "bash",
        },
        {
          label: "Terminal",
          code: [
            "vestra agent setup             # the plan, then y/N",
            "vestra agent setup --dry-run   # the plan only",
            "vestra agent setup --print     # how to do it by hand",
          ].join("\n"),
          lang: "bash",
        },
      ],
    },
    {
      id: "vestra-mcp",
      title: "vestra mcp: your account, from your editor",
      body: [
        "`vestra mcp` is an MCP server on your computer, over stdio, that acts on your account with your key: it sets Bash up, tests it, deploys it to test mode and asks it. It has `search_docs` and `read_doc` too.",
        "The docs MCP only reads the docs, for anyone; `vestra mcp` acts for you, so it runs where your key is. [From your editor](/developers/guides/your-editor) walks through it.",
      ],
      snippets: [
        {
          label: "mcp.json",
          code: JSON.stringify(
            { mcpServers: { vestra: { command: "vestra", args: ["mcp"] } } },
            null,
            2
          ),
          lang: "json",
        },
      ],
    },
    {
      id: "skill",
      title: "The Vestra skill",
      body: [
        "A skill an agent loads when the work is Vestra's: the loop of init, test, deploy and ask, the file, keys, approvals, events and errors, and the docs to read first. `vestra agent setup` installs it, and `vestra agent skill` prints it.",
        "The site serves it too, listed in `/.well-known/skills/index.json` for the tools that install skills from a site.",
      ],
      snippets: [
        {
          label: "Terminal",
          code: [
            "vestra agent skill > SKILL.md",
            `curl ${origin}${SKILLS}/vestra/SKILL.md`,
            `npx skills add ${origin}`,
          ].join("\n"),
          lang: "bash",
        },
      ],
    },
    {
      id: "objective",
      title: "From an objective to a deployment",
      body: [
        'With the skill and an MCP server in place, give your agent the objective in plain words: "Set up Vestra to answer our travellers\' booking questions, test it and deploy it to test mode." It runs `vestra init`, plays the tests, fixes what fails, deploys, wires your code and asks as a user would.',
        "Two steps stay with you. A test key, once: make one on the [console's Keys page](/developers/console/keys), then `vestra login` asks for it without showing it. And going live: `vestra deploy --live` reaches real users and spends real money, so your agent stops and asks you first.",
      ],
      snippets: [
        {
          label: "Yours to run",
          code: [
            "vestra login            # once: your test key",
            "vestra deploy --live    # going live, after you say yes",
          ].join("\n"),
          lang: "bash",
        },
      ],
    },
  ];
}
